import React, { FC } from "react";
import { SearchInput, SearchInputProps } from "../../common/search/SearchInput";
import { queryShopifyProductsForAdmin } from "../../datastore/ShopifyProductDataStore";
import { FilterExpressionType, SortOrder } from "../../../../common/query-filters";
import { ShopifyProduct } from "../../../../common/models";
import { ShopifyProductForAdmin } from "../../../../common/tables/shopify/ShopifyProductForAdmin";

// The shopifyProductsForAdmin GraphQL fragment returns a productListing relation
// that isn't declared on the base ShopifyProduct model.
type ShopifyProductWithListing = ShopifyProduct & {
    productListing?: { id: string } | null;
};

interface ProductSearchInputProps
    extends Omit<SearchInputProps<ShopifyProductForAdmin, ShopifyProduct>, "queryInput" | "query" | "resultsMap"> {
    searchResultSize?: number;
}

export const ProductSearchInput: FC<ProductSearchInputProps> = ({
    searchResultSize = 8,
    placeholderText,
    leftIconContainerProps,
    spinnerProps,
    searchIconProps,
    textInputProps,
    onSelectSearchResult,
}) => {
    return (
        <SearchInput
            placeholderText={placeholderText}
            query={queryShopifyProductsForAdmin}
            queryInput={{
                expressions: [
                    {
                        columnName: "title",
                        columnValue: [""],
                        type: FilterExpressionType.contains,
                        caseInsensitive: true,
                    },
                    // Restrict results to ShopifyProducts that have been linked to
                    // a ProductListing — selecting an unlinked result would navigate
                    // to an empty `/products/` path.
                    {
                        columnName: "productListingId",
                        columnValue: [],
                        type: FilterExpressionType.isNotNull,
                        caseInsensitive: false,
                    },
                ],
                page: 0,
                pageSize: searchResultSize,
                sortOrder: SortOrder.ASCENDING,
                sortColumn: "title",
            }}
            resultsMap={(product: ShopifyProduct) => {
                const productListingId = (product as ShopifyProductWithListing).productListing?.id;
                return {
                    label: product.title ?? String(product.id),
                    value: productListingId ?? "",
                    isVerified: !!productListingId,
                };
            }}
            onSelectSearchResult={onSelectSearchResult}
            leftIconContainerProps={leftIconContainerProps}
            spinnerProps={spinnerProps}
            searchIconProps={searchIconProps}
            textInputProps={textInputProps}
        />
    );
};
